import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import ComplianceOverviewCard from '../components/compliance/ComplianceOverviewCard';
import ComplianceCheckCard from '../components/compliance/ComplianceCheckCard';
import AdsTxtGenerator from '../components/compliance/AdsTxtGenerator';
import RobotsTxtGenerator from '../components/compliance/RobotsTxtGenerator';
import PrivacyPolicyGenerator from '../components/compliance/PrivacyPolicyGenerator';
import FeedbackNotification from '../components/FeedbackNotification';
import usePageMetadata from '../utils/usePageMetadata';
import './Compliance.css';

const STATUS_WEIGHT = {
  pass: 1,
  warning: 0.5,
  fail: 0,
};

const POLL_INTERVAL_MS = 4000;

const scoreChecks = (checks) => {
  const totalChecks = checks.length;
  if (totalChecks === 0) {
    return {
      score: 0,
      statusCounts: { pass: 0, warning: 0, fail: 0 },
      overallStatus: 'fail',
    };
  }

  const statusCounts = checks.reduce(
    (accumulator, check) => {
      accumulator[check.status] += 1;
      return accumulator;
    },
    { pass: 0, warning: 0, fail: 0 }
  );

  const rawScore = checks.reduce((total, check) => {
    return total + STATUS_WEIGHT[check.status] * (100 / totalChecks);
  }, 0);

  const score = Math.round(rawScore);
  const overallStatus =
    statusCounts.fail > 0 ? 'fail' : statusCounts.warning > 0 ? 'warning' : 'pass';

  return { score, statusCounts, overallStatus };
};

const Compliance = () => {
  usePageMetadata({
    title: 'Compliance Dashboard | The Ad Shop',
    description: 'Monitor ad policy compliance, generate ads.txt and robots.txt files, and ensure privacy policy compliance.'
  });

  const { token } = useAuth();
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [checks, setChecks] = useState([]);
  const [loadingSites, setLoadingSites] = useState(true);
  const [loadingReport, setLoadingReport] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [generatedAt, setGeneratedAt] = useState(null);
  const [scanStatus, setScanStatus] = useState('idle');
  const [scanProgress, setScanProgress] = useState(null);
  const [reportScore, setReportScore] = useState(null);
  const [reportStatusCounts, setReportStatusCounts] = useState(null);
  const [startedAt, setStartedAt] = useState(null);
  const [showAdsTxtGenerator, setShowAdsTxtGenerator] = useState(false);
  const [showRobotsTxtGenerator, setShowRobotsTxtGenerator] = useState(false);
  const [showPrivacyPolicyGenerator, setShowPrivacyPolicyGenerator] = useState(false);
  const pollingRef = useRef(null);

  const progressPercent = useMemo(() => {
    if (!scanProgress || !scanProgress.total) {
      return 0;
    }
    return Math.min(100, Math.round((scanProgress.completed / scanProgress.total) * 100));
  }, [scanProgress]);

  const progressCompleted = scanProgress?.completed ?? 0;
  const progressTotal = scanProgress?.total ?? 0;
  const progressMessage = scanProgress?.message || 'Running compliance checks...';
  const progressMeta = progressTotal
    ? `${progressCompleted} of ${progressTotal} checks complete`
    : `${progressCompleted} checks complete`;
  const showScanCard = refreshing || scanStatus === 'running';

  useEffect(() => {
    const fetchSites = async () => {
      if (!token) {
        return;
      }

      setLoadingSites(true);
      setError('');

      try {
        const response = await axios.get('/api/sites', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const fetchedSites = response.data?.sites || [];
        setSites(fetchedSites);

        if (fetchedSites.length > 0) {
          setSelectedSiteId((current) => current || fetchedSites[0].siteId);
        } else {
          setSelectedSiteId('');
          setChecks([]);
        }
      } catch (err) {
        console.error('Error loading sites for compliance:', err);
        setError('Failed to load your sites. Please try again.');
        setSites([]);
        setSelectedSiteId('');
        setChecks([]);
      } finally {
        setLoadingSites(false);
      }
    };

    fetchSites();
  }, [token]);

  const loadComplianceReport = useCallback(
    async (siteId, { silent = false } = {}) => {
      if (!token || !siteId) {
        return null;
      }

      if (!silent) {
        setLoadingReport(true);
        setError('');
      }

      try {
        const response = await axios.get(`/api/compliance?siteId=${siteId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

      const data = response.data || {};
      if (Array.isArray(data.checks) && data.checks.length > 0) {
        setChecks(data.checks);
      }
      if (data.generatedAt) {
        setGeneratedAt(data.generatedAt);
      }
        const reportChecks = Array.isArray(data.checks) ? data.checks : [];
        if (!silent || reportChecks.length > 0) {
          setChecks(reportChecks);
        }
        setGeneratedAt(data.generatedAt || null);

        const nextStatus = data.status || 'completed';
        setScanStatus(nextStatus);
        setScanProgress(data.progress || null);
        setRefreshing(nextStatus === 'running');
        setReportScore(typeof data.score === 'number' ? data.score : null);
        setReportStatusCounts(
          data.statusCounts && typeof data.statusCounts === 'object' ? data.statusCounts : null
        );
        setStartedAt(data.startedAt || null);

        if (!silent || nextStatus === 'error') {
          setError(
            nextStatus === 'error'
              ? data.errorMessage || 'Compliance scan failed. Please try again.'
              : ''
          );
        }

        return data;
      } catch (err) {
        console.error('Error loading compliance report:', err);
        if (!silent) {
          setError('Failed to load compliance report.');
          setChecks([]);
          setGeneratedAt(null);
          setScanStatus('error');
          setScanProgress(null);
          setRefreshing(false);
          setReportScore(null);
          setReportStatusCounts(null);
          setStartedAt(null);
        }
        return null;
      } finally {
        if (!silent) {
          setLoadingReport(false);
        }
      }
    },
    [token]
  );

  useEffect(() => {
    if (!selectedSiteId) {
      return;
    }
    setScanStatus('idle');
    setScanProgress(null);
    setReportScore(null);
    setReportStatusCounts(null);
    setStartedAt(null);
    setChecks([]);
    loadComplianceReport(selectedSiteId);
  }, [selectedSiteId, loadComplianceReport]);

  useEffect(() => {
    if (!selectedSiteId || scanStatus !== 'running') {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      return;
    }

    if (!pollingRef.current) {
      pollingRef.current = setInterval(() => {
        loadComplianceReport(selectedSiteId, { silent: true });
      }, POLL_INTERVAL_MS);
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [selectedSiteId, scanStatus, loadComplianceReport]);

  const fallbackMetrics = useMemo(() => scoreChecks(checks), [checks]);

  const fallbackStatusCounts = fallbackMetrics.statusCounts;
  const effectiveScore = reportScore ?? fallbackMetrics.score;
  const effectiveStatusCounts = useMemo(
    () => ({
      pass: reportStatusCounts?.pass ?? fallbackStatusCounts.pass,
      warning: reportStatusCounts?.warning ?? fallbackStatusCounts.warning,
      fail: reportStatusCounts?.fail ?? fallbackStatusCounts.fail,
    }),
    [reportStatusCounts, fallbackStatusCounts]
  );
  const effectiveOverallStatus =
    effectiveStatusCounts.fail > 0
      ? 'fail'
      : effectiveStatusCounts.warning > 0
      ? 'warning'
      : 'pass';

  const noReportData = !checks.length && scanStatus !== 'running';
  const issuesNeedingAttention =
    (effectiveStatusCounts.fail || 0) + (effectiveStatusCounts.warning || 0);
  let highlight;
  if (scanStatus === 'running') {
    highlight = "Compliance scan in progress. We'll update results as each check finishes.";
  } else if (scanStatus === 'error') {
    highlight = 'We could not complete the last scan. Try running it again.';
  } else if (noReportData) {
    highlight = 'Run your first compliance scan to see results and recommendations.';
  } else if (issuesNeedingAttention === 0) {
    highlight = 'Everything looks great. Keep monitoring for new changes.';
  } else {
    highlight = `Your site is ${effectiveScore}% compliant. ${issuesNeedingAttention} issue${
      issuesNeedingAttention > 1 ? 's' : ''
    } need attention.`;
  }

  const handleSiteChange = (event) => {
    setSelectedSiteId(event.target.value);
  };

  const handleRefresh = async () => {
    if (!selectedSiteId || refreshing) {
      return;
    }

    setRefreshing(true);
    const optimisticTotal =
      scanProgress?.total || (checks?.length && checks.length > 0 ? checks.length : 7);
    setScanStatus('running');
    setScanProgress({
      total: optimisticTotal,
      completed: 0,
      message: 'Preparing compliance scan...',
    });
    setStartedAt(new Date().toISOString());

    const runScan = async () => {
      try {
        const response = await axios.post(
          '/api/compliance',
          { siteId: selectedSiteId },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const data = response.data || {};
        const nextStatus = data.status || 'completed';
        const progressFromPost = data.progress || null;

        setError(
          nextStatus === 'error'
            ? data.errorMessage || 'Compliance scan failed. Please try again.'
            : ''
        );

      if (nextStatus === 'running') {
        setScanStatus('running');
        setScanProgress(
          progressFromPost || {
            total: optimisticTotal,
            completed: Math.min(optimisticTotal - 1, Math.max(1, progressCompleted || 1)),
            message: 'Compliance scan running...',
          }
        );
          if (data.startedAt) {
            setStartedAt(data.startedAt);
          }
          await loadComplianceReport(selectedSiteId, { silent: true });
        } else {
        setScanStatus('completed');
          setScanProgress(
            progressFromPost || {
              total: optimisticTotal,
              completed: optimisticTotal,
            message: 'Scan completed',
            }
          );
          await loadComplianceReport(selectedSiteId, { silent: false });
        }
      } catch (err) {
        console.error('Error refreshing compliance report:', err);
        if (err.response?.status === 429) {
          const data = err.response.data || {};
          const retryAt = data.retryAvailableAt ? new Date(data.retryAvailableAt) : null;
          const formattedRetry = retryAt
            ? retryAt.toLocaleString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })
            : null;
          const cooldownMessage = formattedRetry
            ? `${data.message || 'Compliance scan cooldown in effect.'} Next scan available ${formattedRetry}.`
            : data.message || 'Compliance scan cooldown in effect. Please try again later.';
          setError(cooldownMessage);
        } else {
          setError('Unable to refresh compliance report right now.');
        }
      } finally {
        setRefreshing(false);
      }
    };

    runScan();
  };

  if (loadingSites) {
    return (
      <div className="compliance-page">
        <div className="compliance-loading">
          <div className="spinner" />
          <p>Loading your sites and compliance data...</p>
        </div>
      </div>
    );
  }

  if (!loadingSites && sites.length === 0) {
    return (
      <div className="compliance-page">
        <div className="compliance-empty">
          <h2>No sites connected yet</h2>
          <p>Add a site from the Sites dashboard to run compliance checks.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="compliance-page">
      <main className="compliance-content">
        <div className="compliance-header">
          <div className="header-copy">
            <h1>Compliance dashboard</h1>
            <p>
              Stay ahead of ad policy and privacy requirements. Review core checks, understand
              what&apos;s failing, and fix issues before they impact revenue.
            </p>
          </div>
          <div className="site-selector">
            <label htmlFor="site-selector">Site</label>
            <div className="site-selector-controls">
              <select
                id="site-selector"
                value={selectedSiteId}
                onChange={handleSiteChange}
                disabled={loadingReport && !refreshing}
              >
                {sites.map((site) => (
                  <option key={site.siteId} value={site.siteId}>
                    {site.name || site.url}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="refresh-report-btn"
                onClick={handleRefresh}
                disabled={refreshing || !selectedSiteId}
              >
                {refreshing ? 'Refreshing...' : 'Refresh report'}
              </button>
            </div>
            {generatedAt && (
              <p className="generated-at">Report generated {new Date(generatedAt).toLocaleString()}</p>
            )}
            {showScanCard && (
              <div className="scan-progress">
                <div className="scan-progress-header">
                  <span className="scan-progress-indicator" aria-hidden="true" />
                  <span>
                    {scanStatus === 'running' ? 'Compliance scan in progress' : 'Compliance scan completed'}
                  </span>
                </div>
                {startedAt && (
                  <p className="scan-progress-started">
                    Started {new Date(startedAt).toLocaleString()}
                  </p>
                )}
                <p className="scan-progress-message">{progressMessage}</p>
                <div className="scan-progress-bar">
                  <div
                    className="scan-progress-bar-fill"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
                <span className="scan-progress-meta">{progressMeta}</span>
              </div>
            )}
            {scanStatus === 'error' && (
              <div className="scan-progress scan-progress-error">
                <div className="scan-progress-header">
                  <span className="scan-progress-indicator error" aria-hidden="true" />
                  <span>Compliance scan failed</span>
                </div>
                <p className="scan-progress-message">
                  {error || 'We could not finish the compliance scan. Try again soon.'}
                </p>
              </div>
            )}
          </div>
        </div>

        {error && scanStatus !== 'error' && <div className="compliance-error">{error}</div>}

        <ComplianceOverviewCard
          score={effectiveScore}
          totalChecks={checks.length}
          statusCounts={effectiveStatusCounts}
          highlight={highlight}
          breakdown={{ overallStatus: effectiveOverallStatus }}
        />

        {loadingReport && scanStatus !== 'running' ? (
          <div className="compliance-loading inline">
            <div className="spinner" />
            <p>{refreshing ? 'Refreshing compliance checks...' : 'Loading compliance checks...'}</p>
          </div>
        ) : noReportData ? (
          <div className="compliance-results-empty">
            <h3>No compliance data yet</h3>
            <p>Run a compliance scan to populate these checks.</p>
          </div>
        ) : (
          <section className="compliance-cards">
            {checks.map((check) => {
              // Add onClick handlers for generation buttons
              const checkWithAction = { ...check };
              const selectedSite = sites.find(s => s.siteId === selectedSiteId);
              
              if (check.id === 'ads-txt' && check.action?.href === '#generate-ads-txt') {
                checkWithAction.action = {
                  ...check.action,
                  onClick: () => {
                    if (selectedSite) {
                      setShowAdsTxtGenerator(true);
                    }
                  },
                };
              }
              
              if (check.id === 'robots-txt' && check.action?.href === '#generate-robots-txt') {
                checkWithAction.action = {
                  ...check.action,
                  onClick: () => {
                    if (selectedSite) {
                      setShowRobotsTxtGenerator(true);
                    }
                  },
                };
              }
              
              if (check.id === 'privacy-policy' && check.action?.href === '#generate-privacy-policy') {
                checkWithAction.action = {
                  ...check.action,
                  onClick: () => {
                    if (selectedSite) {
                      setShowPrivacyPolicyGenerator(true);
                    }
                  },
                };
              }
              
              return <ComplianceCheckCard key={check.id} check={checkWithAction} />;
            })}
          </section>
        )}
      </main>
      {showAdsTxtGenerator && (
        <AdsTxtGenerator
          domain={(() => {
            const selectedSite = sites.find(s => s.siteId === selectedSiteId);
            if (selectedSite) {
              try {
                const url = new URL(selectedSite.url.startsWith('http') ? selectedSite.url : `https://${selectedSite.url}`);
                return url.hostname;
              } catch (e) {
                return selectedSite.url.replace(/^https?:\/\//, '').split('/')[0];
              }
            }
            return '';
          })()}
          siteId={selectedSiteId}
          onClose={() => setShowAdsTxtGenerator(false)}
        />
      )}
      {showRobotsTxtGenerator && (
        <RobotsTxtGenerator
          domain={(() => {
            const selectedSite = sites.find(s => s.siteId === selectedSiteId);
            if (selectedSite) {
              try {
                const url = new URL(selectedSite.url.startsWith('http') ? selectedSite.url : `https://${selectedSite.url}`);
                return url.hostname;
              } catch (e) {
                return selectedSite.url.replace(/^https?:\/\//, '').split('/')[0];
              }
            }
            return '';
          })()}
          siteId={selectedSiteId}
          onClose={() => setShowRobotsTxtGenerator(false)}
        />
      )}
      {showPrivacyPolicyGenerator && (
        <PrivacyPolicyGenerator
          domain={(() => {
            const selectedSite = sites.find(s => s.siteId === selectedSiteId);
            if (selectedSite) {
              try {
                const url = new URL(selectedSite.url.startsWith('http') ? selectedSite.url : `https://${selectedSite.url}`);
                return url.hostname;
              } catch (e) {
                return selectedSite.url.replace(/^https?:\/\//, '').split('/')[0];
              }
            }
            return '';
          })()}
          siteId={selectedSiteId}
          onClose={() => setShowPrivacyPolicyGenerator(false)}
        />
      )}
      <FeedbackNotification />
    </div>
  );
};

export default Compliance;


